import { TrendingUp, ArrowRight } from "lucide-react";
import LearnLink from "./LearnLink";

// Lever.category → glossary slug. Categories come from the backend's
// `build_score_levers` output and are stable title-case strings like
// "Liquidity", "Debt service", "Working capital", "Operating leverage".
// New/unknown categories degrade gracefully (no LearnLink rendered).
const LEVER_CATEGORY_SLUG = {
  liquidity: "quick-ratio",
  solvency: "dscr",
  "debt service": "dscr",
  leverage: "debt-to-ebitda",
  "operating leverage": "debt-to-ebitda",
  "interest burden": "interest-coverage",
  profitability: "ebitda-margin",
  "pricing power": "gross-margin",
  margin: "gross-margin",
  efficiency: "cash-conversion-cycle",
  "working capital": "working-capital",
  collections: "dso",
  inventory: "inventory-turnover",
  growth: "rule-of-40",
  "path to profitability": "rule-of-40",
  cash: "cash-conversion-cycle",
};

function leverSlug(category) {
  if (!category) return null;
  const key = String(category).toLowerCase();
  return LEVER_CATEGORY_SLUG[key] || null;
}

/**
 * "Three numbers that will move your score the most" — sensitivity analysis
 * pre-baked from the What-If sandbox logic. Each lever shows the score/grade
 * before and after a realistic move, plus a concrete how-to.
 */
export default function ScoreLeversCard({ levers }) {
  if (!levers || levers.length === 0) return null;
  return (
    <div className="mb-6" data-testid="score-levers-card">
      <div className="bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden">
        <div className="px-6 sm:px-8 py-5 border-b border-[#E5E0D8]">
          <div className="flex items-center gap-2.5">
            <TrendingUp size={16} strokeWidth={1.75} className="text-[#2C5C43]" />
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#2C5C43] font-medium">
              Score levers
            </p>
          </div>
          <p className="font-serif-display text-xl sm:text-2xl text-[#1F382E] mt-1 tracking-tight leading-tight">
            Three moves that would change your grade the most.
          </p>
          <p className="text-[12.5px] text-[#5C665D] leading-relaxed mt-1.5 max-w-2xl">
            Pre-baked sensitivity from the What-If sandbox. Each one shows the math: where you are now,
            where you'd be after the move, and how to get there.
          </p>
        </div>
        <div className="divide-y divide-[#E5E0D8]">
          {levers.map((lever, i) => {
            const slug = leverSlug(lever.category);
            return (
              <div
                key={i}
                className="px-6 sm:px-8 py-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 items-start"
                data-testid={`score-lever-${i}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C665D] font-medium">
                      {lever.category}
                    </p>
                    {slug && (
                      <LearnLink
                        slug={slug}
                        label="Read in 30s"
                        source={`score_lever_${i}`}
                        variant="pill"
                      />
                    )}
                  </div>
                  <p className="font-serif-display text-lg text-[#1F382E] leading-tight">
                    {lever.label}
                  </p>
                  <p className="text-[13px] text-[#5C665D] leading-relaxed mt-1">
                    <span className="tabular-nums">{lever.current_value}</span>{" "}
                    <ArrowRight size={11} strokeWidth={1.5} className="inline mx-1.5 text-[#C26D5C]" />
                    <span className="tabular-nums text-[#1F382E] font-medium">{lever.target_value}</span>
                  </p>
                  <p className="text-[13.5px] text-[#1A1A1A] leading-relaxed mt-2.5">
                    <span className="font-medium text-[#2C5C43]">How:</span> {lever.how}
                  </p>
                </div>
                {/* Score-pill: before → after */}
                <div className="flex items-center gap-3 shrink-0 self-center">
                  <div className="text-center">
                    <p className="text-[9px] uppercase tracking-[0.18em] text-[#5C665D]">Now</p>
                    <p className="font-serif-display text-3xl text-[#5C665D] tabular-nums leading-none mt-1">
                      {lever.grade_before}
                    </p>
                    <p className="text-[10px] text-[#5C665D] mt-1 tabular-nums">{lever.score_before}/100</p>
                  </div>
                  <ArrowRight size={18} strokeWidth={1.5} className="text-[#C26D5C]" />
                  <div className="text-center">
                    <p className="text-[9px] uppercase tracking-[0.18em] text-[#2C5C43]">After</p>
                    <p className="font-serif-display text-3xl text-[#2C5C43] tabular-nums leading-none mt-1">
                      {lever.grade_after}
                    </p>
                    <p className="text-[10px] text-[#2C5C43] mt-1 tabular-nums">{lever.score_after}/100</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
